import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';

import { useAuthStore } from '@features/auth/store/authStore';
import { useAuth } from '@features/auth/hooks/useAuth';
import { createWebSocketEventRouter, type WebSocketEventRouter } from '@/shared/websocket/router';
import { WebSocketEnvelopeSchema } from '@/shared/websocket/types';
import { registerCommonWebSocketHandlers } from '@/shared/websocket/commonEvents';
import { registerTaskWebSocketHandlers } from '@features/tasks/ws/taskEvents';
import { registerInventoryWebSocketHandlers } from '@features/inventory/ws/inventoryEvents';
import { registerMapWebSocketHandlers } from '@features/map/ws/mapEvents';
import { registerDeviceGlobalWebSocketHandlers } from '@features/devices/ws/deviceEvents';
import { registerModuleWebSocketHandlers } from '@features/modules/ws/moduleEvents';
import { registerDashboardWebSocketHandlers } from '@features/dashboard/ws/dashboardEvents';
import { registerCallWebSocketHandlers } from '@features/customers/ws/callEvents';
import { registerBillingWebSocketHandlers } from '@features/settings/ws/billingEvents';
import { ensureNamespaces } from '@/i18n/namespaces';

const WS_ENABLED = import.meta.env.VITE_WS_ENABLED !== 'false';
const DEV_AUTH_TOKEN =
  import.meta.env.DEV ? import.meta.env.VITE_DEV_AUTH_TOKEN || 'dev-local-token' : undefined;
const INITIAL_CONNECT_DELAY_MS = 1_000;
const BASE_RECONNECT_DELAY_MS = 5_000;
const BACKOFF_FACTOR = 1.5;
const MAX_RECONNECT_DELAY_MS = 30_000;
const DEBUG_HANDLER_KEY = '__WORKERNET_WS_DEBUG__';

type RouterRegister = (router: WebSocketEventRouter) => () => void;

interface DynamicHandlerEntry {
  register: RouterRegister;
  dispose: () => void;
}

interface UseWebSocketResult {
  isConnected: boolean;
  send: (data: unknown) => void;
  registerHandlers: (register: RouterRegister) => () => void;
}

export function useWebSocket(): UseWebSocketResult {
  const [isConnected, setIsConnected] = useState(false);

  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(['translation', 'modules']);
  const { message } = App.useApp();
  const { hasPermission } = useAuth();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const routerRef = useRef(createWebSocketEventRouter());
  const dynamicHandlersRef = useRef<DynamicHandlerEntry[]>([]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const cleanupWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    // Убеждаемся, что namespace 'modules' загружен перед использованием
    void ensureNamespaces(['modules']);

    const router = routerRef.current;
    router.clear();

    const disposers: Array<() => void> = [];
    // Всегда регистрируем только общие обработчики
    disposers.push(registerCommonWebSocketHandlers(router, { queryClient, translate: t }));
    // Подписки по правам пользователя (RBAC)
    if (hasPermission?.('tasks.view') || hasPermission?.('tasks.edit')) {
      disposers.push(registerTaskWebSocketHandlers(router, { queryClient, translate: t }));
    }
    if (hasPermission?.('inventory.view') || hasPermission?.('inventory.catalog.manage')) {
      disposers.push(registerInventoryWebSocketHandlers(router, { queryClient, translate: t }));
    }
    // Глобальные device-события (device_status/device_mac_*, минимизированы внутри обработчика).
    // Регистрируем по devices.monitoring: сервер гейтит ровно этим правом всю семью
    // мониторинговых роутов (features/devices/ws/deviceEvents.ts: registerDeviceGlobalWebSocketHandlers
    // обрабатывает только device_status/device_mac_update/device_mac_activity — чисто device-домен,
    // остальные подписки в этом файле не затронуты). equipment.monitoring/equipment.view — права
    // каталога оборудования без серверных гейтов на этом контуре (тот же класс, что и вкладки
    // карточки, docs/superpowers/specs/2026-08-04-device-card-gate-vocabulary.md).
    if (hasPermission?.('devices.monitoring')) {
      disposers.push(registerDeviceGlobalWebSocketHandlers(router, { queryClient, translate: t }));
    }
    // Карта — только при правах на просмотр карты
    if (hasPermission?.('map.view') || hasPermission?.('map.manage')) {
      disposers.push(registerMapWebSocketHandlers(router, { queryClient, translate: t }));
    }
    // Модули — только при правах на просмотр модулей
    if (hasPermission?.('modules.view') || hasPermission?.('modules.manage')) {
      disposers.push(registerModuleWebSocketHandlers(router, { queryClient, translate: t, message }));
    }
    // Биллинг — только при правах на управление модулями
    if (hasPermission?.('modules.manage')) {
      disposers.push(registerBillingWebSocketHandlers(router, { queryClient }));
    }
    // Dashboard — всегда регистрируем (для обновления метрик)
    disposers.push(registerDashboardWebSocketHandlers(router, { queryClient }));
    
    // Входящие звонки — только при правах на просмотр клиентов.
    //
    // ⚠️ ЗДЕСЬ БЫЛИ ДВА ФАНТОМНЫХ ПРАВА: `customer.view` и `customer.manage` — таких в
    // каталоге нет (живые называются `customers.*`). `hasPermission` возвращает true на
    // ЛЮБУЮ строку для администратора и false для всех прочих, поэтому оба операнда были
    // ложны у каждого не-админа: обработчики входящих звонков не регистрировались вовсе,
    // и оператор, который эти звонки и принимает, не видел ни одного уведомления. Видел
    // только администратор. Реестр фантомов вёл эти строки долгом
    // (`frontend_permission_gate_test.go`), снятие строки и есть починка.
    if (hasPermission?.('customers.view') || hasPermission?.('customers.edit')) {
      disposers.push(registerCallWebSocketHandlers(router, { queryClient }));
    }

    dynamicHandlersRef.current.forEach((entry) => {
      entry.dispose();
      entry.dispose = entry.register(router);
    });

    router.setFallback((event) => {
      if (import.meta.env.DEV) {
        console.warn('[WebSocket] Неизвестное событие', event.type, event.data);
      }
    });

    return () => {
      disposers.forEach((dispose) => dispose());
      dynamicHandlersRef.current.forEach((entry) => {
        entry.dispose();
        entry.dispose = () => {};
      });
      router.clear();
    };
  }, [queryClient, t, i18n.language, hasPermission]);

  const handleMessageEvent = useCallback((event: MessageEvent) => {
    const { data } = event;

    const parseEnvelope = (raw: unknown) => {
      const result = WebSocketEnvelopeSchema.safeParse(raw);
      if (!result.success) {
        if (import.meta.env.DEV) {
          console.warn('[WebSocket] Некорректное сообщение', result.error);
        }
        return null;
      }
      return result.data;
    };

    const routeEnvelope = (raw: unknown) => {
      const envelope = parseEnvelope(raw);
      if (!envelope) {
        return;
      }
      routerRef.current.handle(envelope);
    };

    if (typeof data === 'string') {
      // Обработка случая когда приходит несколько JSON-объектов разделенных переносом строки
      const lines = data.split('\n').filter((line) => line.trim() !== '');
      
      for (const line of lines) {
        try {
          const parsed = JSON.parse(line);
          routeEnvelope(parsed);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[WebSocket] Ошибка разбора JSON', error);
            console.error('[WebSocket] Проблемные данные (первые 200 символов):', line.substring(0, 200));
          }
        }
      }
      return;
    }

    if (data instanceof Blob) {
      data
        .text()
        .then((text) => {
          try {
            const parsed = JSON.parse(text);
            routeEnvelope(parsed);
          } catch (error) {
            if (import.meta.env.DEV) {
              console.error('[WebSocket] Ошибка разбора Blob JSON', error);
            }
          }
        })
        .catch((error) => {
          if (import.meta.env.DEV) {
            console.error('[WebSocket] Ошибка чтения Blob', error);
          }
        });
      return;
    }

    if (import.meta.env.DEV) {
      console.warn('[WebSocket] Неподдерживаемый тип данных', typeof data);
    }
  }, []);

  const connect = useCallback(() => {
    if (!WS_ENABLED) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    const storedToken = localStorage.getItem('access_token')?.trim();
    // В dev приоритетно используем bypass-токен, чтобы не зависеть от устаревших access_token
    const token = DEV_AUTH_TOKEN ?? storedToken;
    if (!token) {
      setIsConnected(false);
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/v1/ws?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
        clearReconnectTimer();
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;

        const attempt = reconnectAttempts.current;
        const delay = Math.min(
          BASE_RECONNECT_DELAY_MS * Math.pow(BACKOFF_FACTOR, attempt),
          MAX_RECONNECT_DELAY_MS,
        );

        reconnectAttempts.current = attempt + 1;
        clearReconnectTimer();
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, delay);
      };

      ws.onerror = (event) => {
        if (import.meta.env.DEV) {
          console.error('[WebSocket] Ошибка соединения', event);
        }
      };

      ws.onmessage = handleMessageEvent;

      wsRef.current = ws;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[WebSocket] Не удалось создать соединение', error);
      }

      const attempt = reconnectAttempts.current;
      const delay = Math.min(
        BASE_RECONNECT_DELAY_MS * Math.pow(BACKOFF_FACTOR, attempt),
        MAX_RECONNECT_DELAY_MS,
      );

      reconnectAttempts.current = attempt + 1;
      clearReconnectTimer();
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
    }
  }, [clearReconnectTimer, handleMessageEvent]);

  useEffect(() => {
    if (!WS_ENABLED) {
      cleanupWebSocket();
      return undefined;
    }

    if (!isAuthenticated) {
      cleanupWebSocket();
      reconnectAttempts.current = 0;
      clearReconnectTimer();
      return undefined;
    }

    const initialTimeout = setTimeout(() => {
      reconnectAttempts.current = 0;
      connect();
    }, INITIAL_CONNECT_DELAY_MS);

    return () => {
      clearTimeout(initialTimeout);
      clearReconnectTimer();
      cleanupWebSocket();
    };
  }, [clearReconnectTimer, cleanupWebSocket, connect, isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined' || import.meta.env.PROD) {
      return undefined;
    }

    const debugWindow = window as typeof window & Record<string, unknown>;
    const debugHandler = (payload: unknown) => {
      const result = WebSocketEnvelopeSchema.safeParse(payload);
      if (!result.success) {
        if (import.meta.env.DEV) {
          console.warn('[WebSocket] Debug payload неверен', result.error);
        }
        return;
      }
      routerRef.current.handle(result.data);
    };

    debugWindow[DEBUG_HANDLER_KEY] = debugHandler;

    return () => {
      if (debugWindow[DEBUG_HANDLER_KEY] === debugHandler) {
        delete debugWindow[DEBUG_HANDLER_KEY];
      }
    };
  }, []);

  const registerHandlers = useCallback(
    (register: RouterRegister) => {
      const entry: DynamicHandlerEntry = {
        register,
        dispose: register(routerRef.current),
      };
      dynamicHandlersRef.current.push(entry);

      return () => {
        entry.dispose();
        dynamicHandlersRef.current = dynamicHandlersRef.current.filter((item) => item !== entry);
      };
    },
    [],
  );

  const send = useCallback((data: unknown) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else if (import.meta.env.DEV) {
      console.warn('[WebSocket] Попытка отправить сообщение при незавершённом соединении');
    }
  }, []);

  return {
    isConnected,
    send,
    registerHandlers,
  };
}

